import React, { useState } from 'react'
import { useQuery } from 'react-query'
import {
  Activity,
  Search,
  Filter,
  Download,
  Eye,
  User,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'
import { AuditLog } from '../types'

export default function AuditPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({
    action_type: '',
    admin_user: '',
    date_from: '',
    date_to: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)

  const { data: auditLogsData, isLoading } = useQuery(
    ['audit-logs', page, filters],
    () => adminApi.getAuditLogs(page, 50),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  )

  const auditLogs = auditLogsData?.data || []

  const handleExport = async () => {
    try {
      const blob = await adminApi.generateReport('audit-log', { filters, page })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Audit Logs
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Track all administrative actions and system events
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={handleExport}
            className="btn-secondary"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Logs
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by admin user, action type, or resource..."
                  className="input pl-10"
                  value={filters.admin_user}
                  onChange={(e) => setFilters({ ...filters, admin_user: e.target.value })}
                />
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <select
                className="input"
                value={filters.action_type}
                onChange={(e) => setFilters({ ...filters, action_type: e.target.value })}
              >
                <option value="">All Actions</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="user_update">User Update</option>
                <option value="user_delete">User Delete</option>
                <option value="credit_adjustment">Credit Adjustment</option>
                <option value="system_config">System Config</option>
                <option value="workflow_upload">Workflow Upload</option>
              </select>

              <input
                type="date"
                className="input"
                placeholder="From Date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />

              <input
                type="date"
                className="input"
                placeholder="To Date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />

              <button
                onClick={() => setFilters({ action_type: '', admin_user: '', date_from: '', date_to: '' })}
                className="btn-secondary"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Admin User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>IP Address</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <AuditLogRow 
                    key={log.id} 
                    log={log} 
                    onViewDetails={() => setSelectedLog(log)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {auditLogs.length === 0 && (
            <div className="text-center py-12">
              <Activity className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No audit logs found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No audit logs match your current filters.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {auditLogsData?.total_pages && auditLogsData.total_pages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={auditLogsData.total_pages}
          onPageChange={setPage}
        />
      )}

      {/* Audit Log Detail Modal */}
      {selectedLog && (
        <AuditLogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  )
}

interface AuditLogRowProps {
  log: AuditLog
  onViewDetails: () => void
}

function AuditLogRow({ log, onViewDetails }: AuditLogRowProps) {
  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'login':
      case 'logout':
        return <User className="h-4 w-4 text-blue-500" />
      case 'user_update':
      case 'user_delete':
        return <Shield className="h-4 w-4 text-orange-500" />
      case 'credit_adjustment':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'system_config':
        return <AlertTriangle className="h-4 w-4 text-red-500" />
      default:
        return <Activity className="h-4 w-4 text-gray-500" />
    }
  }

  const getActionBadge = (actionType: string) => {
    const badgeClass = {
      login: 'badge-success',
      logout: 'badge-gray',
      user_update: 'badge-warning',
      user_delete: 'badge-danger',
      credit_adjustment: 'badge-success',
      system_config: 'badge-warning'
    }[actionType] || 'badge-gray'

    return <span className={`badge ${badgeClass}`}>{actionType.replace('_', ' ')}</span>
  }

  return (
    <tr>
      <td>
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-gray-400" />
          <span className="text-sm">
            {new Date(log.created_at).toLocaleString()}
          </span>
        </div>
      </td>
      <td>
        <div className="font-medium text-gray-900">{log.admin_username}</div>
      </td>
      <td>
        <div className="flex items-center space-x-2">
          {getActionIcon(log.action_type)}
          {getActionBadge(log.action_type)}
        </div>
      </td>
      <td>
        <div className="text-sm">
          {log.resource_type && (
            <span className="font-medium">{log.resource_type}</span>
          )}
          {log.resource_id && (
            <span className="text-gray-500 ml-1">({log.resource_id.slice(0, 8)}...)</span>
          )}
        </div>
      </td>
      <td>
        <span className="font-mono text-sm text-gray-600">{log.ip_address}</span>
      </td>
      <td>
        <span className="badge badge-success">Success</span>
      </td>
      <td>
        <button
          onClick={onViewDetails}
          className="text-primary-600 hover:text-primary-900"
        >
          <Eye className="h-4 w-4" />
        </button>
      </td>
    </tr>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const pages = []
  const maxVisible = 5

  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
  let endPage = Math.min(totalPages, startPage + maxVisible - 1)

  if (endPage - startPage + 1 < maxVisible) {
    startPage = Math.max(1, endPage - maxVisible + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="btn-secondary"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="btn-secondary"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Page <span className="font-medium">{currentPage}</span> of{' '}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            {pages.map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  page === currentPage
                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  )
}

interface AuditLogDetailModalProps {
  log: AuditLog
  onClose: () => void
}

function AuditLogDetailModal({ log, onClose }: AuditLogDetailModalProps) {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-w-2xl">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Audit Log Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Timestamp</label>
              <p className="text-sm text-gray-900">{new Date(log.created_at).toLocaleString()}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Admin User</label>
              <p className="text-sm text-gray-900">{log.admin_username}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Action Type</label>
              <p className="text-sm text-gray-900">{log.action_type.replace('_', ' ')}</p>
            </div>
            
            {log.resource_type && (
              <div>
                <label className="text-sm font-medium text-gray-700">Resource</label>
                <p className="text-sm text-gray-900">
                  {log.resource_type} {log.resource_id && `(${log.resource_id})`}
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-700">IP Address</label>
              <p className="text-sm text-gray-900 font-mono">{log.ip_address}</p>
            </div>
            
            {log.user_agent && (
              <div>
                <label className="text-sm font-medium text-gray-700">User Agent</label>
                <p className="text-sm text-gray-900 break-all">{log.user_agent}</p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-700">Details</label>
              <pre className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md overflow-auto max-h-40">
                {JSON.stringify(log.details, null, 2)}
              </pre>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}